/** Two-column auth layout (OutlierOS components/ui/sign-in.tsx AuthShell): form left, product panel right. */
import type { ReactNode } from 'react';
import { Bot, Flame, Mic } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { BrandLogo } from '../brand';
import { hostsFor } from '@vw/core/src/brand';

/** A page on the owner-built main website (Terms, Privacy policy); local dev has no website, so it stays relative. */
export function websiteLink(path: string): string {
  const h = location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') return path;
  return `https://${hostsFor(h.includes('-staging.') ? 'staging' : 'production').landing}${path}`;
}

export function AuthShell({ children }: { children: ReactNode }) {
  const points = [
    { icon: Flame, title: 'A Wise Score for every reel', text: "Views ÷ the creator's usual views, so you see which reels really broke out." },
    { icon: Mic, title: 'Hooks and transcripts', text: 'The spoken hook and full script of any reel, in the language it was spoken.' },
    { icon: Bot, title: 'Works with Claude and ChatGPT', text: 'Ask your AI assistant about the creators you research.' },
  ];
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col px-6 py-6 sm:px-10">
        <div className="flex items-center gap-2 font-bold"><BrandLogo size={28} />{BRAND.name}</div>
        <div className="flex flex-1 items-center justify-center py-10"><div className="w-full max-w-sm">{children}</div></div>
        <p className="text-center text-xs text-muted-foreground"><a className="underline" href={websiteLink('/terms')}>Terms</a> · <a className="underline" href="/privacy-notice">Privacy notice</a> · <a className="underline" href={websiteLink('/privacy')}>Privacy policy</a></p>
      </div>
      <div className="hidden flex-col justify-center border-l border-border bg-muted px-14 lg:flex">
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Instagram Reels research</p>
        <h2 className="mt-3 max-w-md text-3xl font-black leading-tight">Find the reels that broke out, and the hooks behind them.</h2>
        <ul className="mt-8 space-y-5">
          {points.map((p) => (
            <li key={p.title} className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><p.icon className="h-4 w-4" /></span>
              <span><span className="block text-sm font-bold">{p.title}</span><span className="block text-sm text-muted-foreground">{p.text}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function GoogleButton({ href, label = 'Continue with Google' }: { href: string; label?: string }) {
  return (
    <a href={href} className="flex h-11 w-full items-center justify-center gap-3 rounded-xl border border-border bg-card text-sm font-semibold shadow-xs transition-colors hover:bg-secondary">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z"/><path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/></svg>
      {label}
    </a>
  );
}
